import { Section } from '@jbc-year-end-adj/2024/components/ui/Section';
import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { Grid } from './Grid';
import ActionButton from 'jbc-front/components/ActionButton';
import styles from './ResidentCard.scss';
import { Copy } from 'jbc-front/components/icons';
import { PostcodeInput } from 'components/form/PostcodeInput';
import { Row } from 'components/form/Row';
import { SelectField } from '@jbc-year-end-adj/2024/components/react-hook-form/SelectField';
import { Input } from 'components/form/Input';
import { usePrefectureOptions } from '@jbc-year-end-adj/2024/hooks/usePrefectureOptions';
import { useFormContext } from 'react-hook-form';
import React, { FC, useEffect, useState } from 'react';
import { suppressFetchAddressError, useFetchAddressForEmployee } from '@jbc-year-end-adj/2024/hooks/useAddress';
import { Schema } from './schema';

export const ResidentCard: FC = () => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = useFormContext<Schema>();
  const { prefectureOptions } = usePrefectureOptions();
  const [shouldFetchAddress, setShouldFetchAddress] = useState<boolean>(false);
  const fetchAddress = useFetchAddressForEmployee();
  const postcode0 = watch('residentCard.postcode0');
  const postcode1 = watch('residentCard.postcode1');

  useEffect(() => {
    if (shouldFetchAddress) {
      if (postcode0 && postcode1) {
        let cancelled = false;
        fetchAddress(`${postcode0}${postcode1}`)
          .catch(suppressFetchAddressError)
          .then(address => {
            if (cancelled || !address) return;

            const prefecture = prefectureOptions.find(option => option.label === address.prefecture);
            setValue('residentCard.prefectureId', prefecture?.value ?? '');
            setValue('residentCard.city', address.city);
            setValue('residentCard.street', address.street);

            trigger(['residentCard.prefectureId', 'residentCard.city', 'residentCard.street']);
          });
        return () => {
          cancelled = true;
        };
      }
    } else {
      setShouldFetchAddress(true);
    }
    return;
  }, [postcode0, postcode1]);

  const copyAddress = () => {
    setShouldFetchAddress(false);
    setValue('residentCard.postcode0', watch('basicInfos.postcode0'));
    setValue('residentCard.postcode1', watch('basicInfos.postcode1'));
    setValue('residentCard.prefectureId', watch('basicInfos.prefectureId'));
    setValue('residentCard.city', watch('basicInfos.city'));
    setValue('residentCard.street', watch('basicInfos.street'));
    setValue('residentCard.building', watch('basicInfos.building'));
  };

  return (
    <Section>
      <Section.Header>住民票住所</Section.Header>
      <Section.Body>
        <FormSection>
          <Grid>
            <ActionButton className={styles.copyAddress} icon={<Copy size={13} />} onClick={copyAddress}>
              現住所をコピーする
            </ActionButton>
          </Grid>

          <PostcodeInput
            required
            note="住民票住所をご確認ください。"
            firstCodeProps={{ ...register('residentCard.postcode0'), isError: !!errors?.residentCard?.postcode0?.message }}
            lastCodeProps={{ ...register('residentCard.postcode1'), isError: !!errors?.residentCard?.postcode1?.message }}
            errors={[errors?.residentCard?.postcode0?.message ?? '', errors?.residentCard?.postcode1?.message ?? ''].filter(error => error)}
          />

          <Row>
            <SelectField label="都道府県" options={prefectureOptions} name="residentCard.prefectureId" required />
            <Input
              label="市区町村"
              {...register('residentCard.city')}
              isError={!!errors?.residentCard?.city?.message}
              error={errors?.residentCard?.city?.message}
              required
            />
          </Row>

          <Row>
            <Input label="丁目番地号" {...register('residentCard.street')} />
            <Input label="建物名" {...register('residentCard.building')} />
          </Row>
        </FormSection>
      </Section.Body>
    </Section>
  );
};
